
import Vue from 'vue';
import { TranslateResult } from 'vue-i18n/types';

import MISC_DATA from '@/store/modules/MiscDataModule';
import MAP from '@/store/modules/MapModule';

import RibbonBanner from '@/components/general/utils/RibbonBanner.vue';

import { ITruckFilterSettings } from '@/types/truck';
import { EventBus } from '@/utils/eventBus';
import { routesNames } from '@/router';

interface StringIndex {
	[key: string]: any;
}

export default Vue.extend({
	name: 'PricingCard',

	components: { RibbonBanner },

	mixins: [],

	props: {
		size: {
			type: String,
			default: 'L'
		}
	},

	data() {
		return {};
	},

	computed: {
		MISC_DATA: () => MISC_DATA,

		truckSizeInM3(): any {
			return this.MISC_DATA.data_per_bu.trucks.min_size_in_m3;
		},

		truckSizeData(): any {
			return this.MISC_DATA.basedata.size_groups;
		},

		getAltText(): TranslateResult {
			if (this.isLandingPage) {
				return this.$t('cust.home.price_card.seo_desc', {
					size: this.size,
					price: this.$n(this.landingPageTruckPrice6, this.isInt(this.landingPageTruckPrice6) ? 'currencyShort' : 'currency')
				});
			}

			return this.$t('cust.home.price_card.seo_desc', {
				size: this.size,
				price: this.$n(this.oneSlotPromoPrice, this.isInt(this.oneSlotPromoPrice) ? 'currencyShort' : 'currency')
			});
		},

		oneSlotOfftimeEnabled(): boolean {
			if (!this.MISC_DATA.basedata.features) {
				return false;
			}

			if (!this.MISC_DATA.basedata.features.offtime_slots) {
				return false;
			}

			return this.MISC_DATA.basedata.features.offtime_slots.length > 0;
		},

		oneSlotPromoPrice(): number {
			if (this.MISC_DATA.truck_min_price[this.size].price_6 !== 0) {
				return this.MISC_DATA.truck_min_price[this.size].price_6;
			}

			// fallback old logic

			const prices = this.MISC_DATA.prices;
			const size = this.size.toLowerCase();
			const price_per_size = {
				l: this.oneSlotOfftimeEnabled ? prices.truck1_6off : prices.truck1_6,
				xl: this.oneSlotOfftimeEnabled ? prices.truck2_6off : prices.truck2_6,
				xxl: this.oneSlotOfftimeEnabled ? prices.truck3_6off : prices.truck3_6
			} as StringIndex;
			return price_per_size[size];
		},

		bps(): { icon: string; text: TranslateResult; altText: TranslateResult }[] {
			const bps = [];
			let size_text = '' as TranslateResult;

			if (this.size === 'XXL') {
				size_text = this.$t('home.price_card.size_from', [this.truckSizeData[this.size.toLowerCase()].threshold]);
			} else {
				size_text = this.$t('home.price_card.size_up_to', [this.truckSizeData[this.size.toLowerCase()].threshold]);
			}

			bps.push({
				icon: '/img/pricing_cards/icons/icon_kilometer.svg',
				text: this.$t('home.price_card.free_km'),
				altText: this.$t('cust.home.price_card.free_km_icon')
			});

			if (this.$getDomain() === 'at') {
				bps.push({
					icon: '/img/pricing_cards/icons/icon_vignette.svg',
					text: this.$t('home.price_card.vignette'),
					altText: this.$t('cust.home.price_card.vignette_icon')
				});
			}

			bps.push({
				icon: '/img/pricing_cards/icons/icon_size.svg',
				text: size_text,
				altText: this.$t('cust.home.price_card.size_icon')
			});

			bps.push({
				icon: '/img/pricing_cards/icons/icon_support.svg',
				text: this.$t('home.price_card.breakdown'),
				altText: this.$t('cust.home.price_card.breakdown_icon')
			});

			bps.push({
				icon: '/img/pricing_cards/icons/icon_license.svg',
				text: this.$t('home.price_card.drivers_license'),
				altText: this.$t('cust.home.price_card.drivers_license_icon')
			});

			return bps;
		},

		image(): string {
			if (!this.MISC_DATA.data_per_bu?.images?.home_pricing_cards) {
				return '';
			}
			const images = this.MISC_DATA.data_per_bu.images.home_pricing_cards;
			switch (this.size.toUpperCase()) {
				case 'L':
					return images.l;
				case 'XL':
					return images.xl;
				case 'XXL':
					return images.xxl;
			}
			return '';
		},

		landingPageCardDisabled(): boolean {
			if (this.isLandingPage) {
				if (this.landingPageTruckPrices[this.size].price_24 === 0 && this.landingPageTruckPrices[this.size].price_6 === 0) {
					return true;
				}
			}

			return false;
		},

		cardDisabled(): boolean {
			if (this.landingPageCardDisabled) {
				return true;
			}

			const ribbon_value = this.truckSizeData[this.size.toLowerCase()].banner;
			return ribbon_value === 'coming_soon';
		},

		showCard(): boolean {
			if (!this.isLandingPage) {
				return true;
			}

			if (this.landingPageCardDisabled) {
				return false;
			}

			return true;
		},

		price(): number {
			if (this.MISC_DATA.truck_min_price[this.size].price_24 !== 0) {
				return this.MISC_DATA.truck_min_price[this.size].price_24;
			}

			// fallback old logic

			const keys = {
				L: 'truck1',
				XL: 'truck2',
				XXL: 'truck3'
			} as StringIndex;

			const price_key = keys[this.size];

			return MISC_DATA.prices[price_key];
		},

		ribbonText(): string {
			if (this.landingPageCardDisabled) {
				return this.$t('msg.coming_soon') as string;
			}

			if (this.isLandingPage) {
				return '';
			}

			const ribbon_value = this.truckSizeData[this.size.toLowerCase()].banner;
			let translation_key = '';
			switch (ribbon_value) {
				case 'best_offer':
					translation_key = 'ribbon.top_offer';
					break;
				case 'coming_soon':
					translation_key = 'msg.coming_soon';
			}
			return this.$t(translation_key) as string;
		},

		isExtraSmallDevice(): boolean {
			return this.$vuetify.breakpoint.width < 400;
		},

		isSmallDevice(): boolean {
			return this.$vuetify.breakpoint.width >= 400 && this.$vuetify.breakpoint.width < 600;
		},

		isMediumDevice(): boolean {
			return this.$vuetify.breakpoint.width >= 600 && this.$vuetify.breakpoint.width < 959;
		},

		isSmallLaptop(): boolean {
			return this.$vuetify.breakpoint.mdOnly;
		},

		getCurrentDeviceSize(): any {
			if (this.isExtraSmallDevice) {
				return { width: 300, height: 160 };
			} else if (this.isSmallDevice) {
				return { width: 415, height: 210 };
			} else if (this.isMediumDevice) {
				return { width: 450, height: 240 };
			} else if (this.isSmallLaptop) {
				return { width: 230, height: 100 };
			} else {
				return { width: 335, height: 165 };
			}
		},

		isLandingPage(): boolean {
			return this.$route.name === routesNames.landing_page;
		},

		landingPageTruckPrices(): any {
			return this.MISC_DATA.current_LP_data?.min_prices;
		},

		landingPageTruckPrice24(): number {
			if (this.landingPageTruckPrices[this.size].price_24 !== 0) {
				return this.landingPageTruckPrices[this.size].price_24;
			}

			return 0;
		},

		landingPageTruckPrice6(): number {
			if (this.landingPageTruckPrices[this.size].price_6 !== 0) {
				return this.landingPageTruckPrices[this.size].price_6;
			}

			return 0;
		},

		isMoreThanOneTruck(): boolean {
			if (this.isLandingPage) {
				let count = 0;
				const data = this.landingPageTruckPrices;

				for (const key in data) {
					if (data[key].price_24 === 0 && data[key].price_6 === 0) {
						count++;
					}
				}
				return count < 2;
			}

			return true;
		},

		missingSize(): any {
			if (this.isLandingPage) {
				if (this.landingPageTruckPrice24 === 0 && this.landingPageTruckPrice6 === 0) {
					return this.size;
				}
			}
			return '';
		}
	},

	watch: {
		missingSize: {
			handler(newValue: string, _oldValue: string) {
				if (newValue) {
					this.$emit('missingSize', newValue);
				}
			},
			immediate: true
		}
	},
	methods: {
		isInt(n: any): boolean {
			return Number(n) === n && n % 1 === 0;
		},
		fetchTruckList() {
			const newFilter = new ITruckFilterSettings();
			newFilter.sizes = [this.size];
			newFilter.max_price = '';
			MAP.updateCurrentFilter(newFilter);
			EventBus.$emit('fetch-trucks');
			this.scrollToMap();
		},
		scrollToMap() {
			this.$vuetify.goTo('#map-view', { duration: 640, easing: 'easeInOutCubic' });
		}
	}
});
